/**
 * Financial Aid Value Objects: Documents and Certification
 *
 * Sections 15-17: Additional Information, Document Checklist, Certification
 *
 * @module financial-aid-domain/value-objects/financial-aid/documents-certification
 */
import { z } from 'zod';
import { RequiredFinancialDocumentType } from '../../types/enums';

// ============================================================================
// Section 15: Additional Information
// ============================================================================

const BOUNDED_TEXT_MAX_LENGTH = 4000;
const PARENT_COLLECTED_SNAPSHOT_MAX_JSON_LENGTH = 64_000;

const BoundedTextValueSchema = z.string().max(BOUNDED_TEXT_MAX_LENGTH);
const BoundedTextSchema = BoundedTextValueSchema.optional();
const SnapshotNumberValueSchema = z.number().finite();
const SnapshotScalarSchema = z.union([BoundedTextValueSchema, z.boolean(), SnapshotNumberValueSchema]);
const SnapshotNumberishSchema = z.union([BoundedTextValueSchema, SnapshotNumberValueSchema]);
const IncomeSnapshotSchema = z.object({
  gross2024: SnapshotNumberishSchema.optional(),
  parsonage: SnapshotNumberishSchema.optional(),
  fedTax: SnapshotNumberishSchema.optional(),
  stateTax: SnapshotNumberishSchema.optional(),
  ficaTax: SnapshotNumberishSchema.optional(),
  refunds: SnapshotNumberishSchema.optional(),
  estimated2025: SnapshotNumberishSchema.optional(),
}).partial();
const BankAccountSnapshotSchema = z.object({
  bank: BoundedTextSchema,
  accountNumber: BoundedTextSchema,
  balance: SnapshotNumberishSchema.optional(),
}).partial();
const InvestmentSnapshotSchema = z.object({
  id: BoundedTextSchema,
  description: BoundedTextSchema,
  value: SnapshotNumberishSchema.optional(),
}).partial();
const CampSnapshotSchema = z.object({
  id: BoundedTextSchema,
  childName: BoundedTextSchema,
  campName: BoundedTextSchema,
  fee: SnapshotNumberishSchema.optional(),
}).partial();
const OtherChildSnapshotSchema = z.object({
  id: BoundedTextSchema,
  name: BoundedTextSchema,
  age: BoundedTextSchema,
  grade: BoundedTextSchema,
  school: BoundedTextSchema,
  fullTuition: SnapshotNumberishSchema.optional(),
  scholarship: SnapshotNumberishSchema.optional(),
}).partial();
const VehicleSnapshotSchema = z.object({
  id: BoundedTextSchema,
  year: BoundedTextSchema,
  make: BoundedTextSchema,
  model: BoundedTextSchema,
  value: SnapshotNumberishSchema.optional(),
  debt: SnapshotNumberishSchema.optional(),
  payment: SnapshotNumberishSchema.optional(),
}).partial();
const ContractSnapshotSchema = z.object({
  childrenCount: SnapshotNumberishSchema.optional(),
  totalTuition: SnapshotNumberishSchema.optional(),
  totalFees: SnapshotNumberishSchema.optional(),
  preschoolTuition: SnapshotNumberishSchema.optional(),
  eligibleTuition: SnapshotNumberishSchema.optional(),
}).partial();
const ScholarshipRequestSnapshotSchema = z.object({
  requestedAmount: SnapshotNumberishSchema.optional(),
  offeredTuition: SnapshotNumberishSchema.optional(),
  offeredFees: SnapshotNumberishSchema.optional(),
}).partial();
const AssetsSnapshotSchema = z.object({
  housingType: BoundedTextSchema,
  purchasePrice: SnapshotNumberishSchema.optional(),
  purchaseDate: BoundedTextSchema,
  mortgageBalance: SnapshotNumberishSchema.optional(),
  mortgagePayment: SnapshotNumberishSchema.optional(),
  mortgageYears: SnapshotNumberishSchema.optional(),
  propertyTaxes: SnapshotNumberishSchema.optional(),
  interestRate: SnapshotNumberishSchema.optional(),
  rentPayment: SnapshotNumberishSchema.optional(),
  utilities: SnapshotNumberishSchema.optional(),
  additionalProperties: BoundedTextSchema,
}).partial();
const ExpensesSnapshotSchema = z.object({
  housing: SnapshotNumberishSchema.optional(),
  taxesIncluded: z.boolean().optional(),
  utilities: SnapshotNumberishSchema.optional(),
  studentLoans: SnapshotNumberishSchema.optional(),
  food: SnapshotNumberishSchema.optional(),
  tuition: SnapshotNumberishSchema.optional(),
  bankLoans: SnapshotNumberishSchema.optional(),
  lifeInsurance: SnapshotNumberishSchema.optional(),
  shulDues: SnapshotNumberishSchema.optional(),
  carInsurance: SnapshotNumberishSchema.optional(),
  healthInsurance: SnapshotNumberishSchema.optional(),
  cleaningHelp: SnapshotNumberishSchema.optional(),
  other: SnapshotNumberishSchema.optional(),
  medicalCoverage: SnapshotScalarSchema.optional(),
  medicalCoverageDetails: SnapshotScalarSchema.optional(),
}).partial();
const MajorExpSnapshotSchema = z.object({
  homeImprovements: SnapshotNumberishSchema.optional(),
  homeImprovementsDetails: BoundedTextSchema,
  largePurchases: SnapshotNumberishSchema.optional(),
  largePurchasesDetails: BoundedTextSchema,
  extraordinaryObligations: BoundedTextSchema,
  simchaType: BoundedTextSchema,
  simchaCost: SnapshotNumberishSchema.optional(),
  servicesToYST: z.boolean().optional(),
  servicesDescription: BoundedTextSchema,
  loanAmount: SnapshotNumberishSchema.optional(),
}).partial();
const ParentPersonalSnapshotSchema = z.object({
  title: BoundedTextSchema,
  firstName: BoundedTextSchema,
  lastName: BoundedTextSchema,
  address: BoundedTextSchema,
  city: BoundedTextSchema,
  state: BoundedTextSchema,
  zip: BoundedTextSchema,
  cellPhone: BoundedTextSchema,
  homePhone: BoundedTextSchema,
  email: BoundedTextSchema,
  occupation: BoundedTextSchema,
  employer: BoundedTextSchema,
  businessName: BoundedTextSchema,
  shul: BoundedTextSchema,
  rabbi: BoundedTextSchema,
  isUnemployed: z.boolean().optional(),
  isDivorced: z.boolean().optional(),
  unemploymentWeeklyAmount: SnapshotNumberishSchema.optional(),
}).partial();
// INT-589: the parent form persists the single/two-parent household selection in
// the raw snapshot so the choice survives a draft save/resume round-trip. Values
// are normalized client-side, so the schema only bounds the text payload.
const HouseholdCompositionSnapshotSchema = z.object({
  mode: BoundedTextSchema,
  primaryKey: BoundedTextSchema,
  primaryRole: BoundedTextSchema,
  otherRoleLabel: BoundedTextSchema,
}).partial();
// INT-589/INT-636: signature capture state must round-trip for same-cycle draft
// resume. Cross-cycle hydration strips this key server-side so a prior year's
// certification never pre-fills a new application.
const SignaturesSnapshotSchema = z.object({
  fatherCaptured: z.boolean().optional(),
  fatherCapturedAt: BoundedTextSchema,
  motherCaptured: z.boolean().optional(),
  motherCapturedAt: BoundedTextSchema,
  date: BoundedTextSchema,
  attestations: z.record(z.string().max(200), z.boolean()).optional(),
}).partial();
const ParentCollectedFinancialAidSnapshotSchema = z.object({
  version: z.number().int().positive().optional(),
  householdComposition: HouseholdCompositionSnapshotSchema.optional(),
  signatures: SignaturesSnapshotSchema.optional(),
  father: ParentPersonalSnapshotSchema.optional(),
  mother: ParentPersonalSnapshotSchema.optional(),
  incomeFather: IncomeSnapshotSchema.optional(),
  incomeMother: IncomeSnapshotSchema.optional(),
  benefits: z.object({
    healthInsurance: BoundedTextSchema,
    tuition: BoundedTextSchema,
    auto: BoundedTextSchema,
    other: BoundedTextSchema,
    gifts: z.boolean().optional(),
    giftsAmount: BoundedTextSchema,
  }).partial().optional(),
  assets: AssetsSnapshotSchema.optional(),
  vehicles: z.array(VehicleSnapshotSchema).max(24).optional(),
  investments: z.array(InvestmentSnapshotSchema).max(48).optional(),
  savings: BankAccountSnapshotSchema.optional(),
  checking: BankAccountSnapshotSchema.optional(),
  childrenAssets: z.array(InvestmentSnapshotSchema).max(48).optional(),
  expenses: ExpensesSnapshotSchema.optional(),
  otherChildren: z.array(OtherChildSnapshotSchema).max(48).optional(),
  majorExp: MajorExpSnapshotSchema.optional(),
  camps2025: z.array(CampSnapshotSchema).max(24).optional(),
  camps2024: z.array(CampSnapshotSchema).max(24).optional(),
  contract: ContractSnapshotSchema.optional(),
  request: ScholarshipRequestSnapshotSchema.optional(),
  vacation: z.object({
    traveled: z.boolean().optional(),
    destinations: BoundedTextSchema,
    cost: BoundedTextSchema,
    parentsTraveled: z.boolean().optional(),
    childrenTraveled: z.boolean().optional(),
  }).partial().optional(),
}).partial().superRefine((snapshot, context) => {
  const serialized = JSON.stringify(snapshot);
  if (serialized.length > PARENT_COLLECTED_SNAPSHOT_MAX_JSON_LENGTH) {
    context.addIssue({
      code: 'custom',
      message: 'Parent-collected financial aid snapshot is too large.',
    });
  }
});

const StaffCorrectionAuditChangeSchema = z.object({
  path: z.string().trim().min(1),
  previousValue: z.unknown().nullable().optional(),
  newValue: z.unknown().nullable().optional(),
});

const StaffCorrectionAuditEntrySchema = z.object({
  id: z.string().min(1),
  actorId: z.string().min(1),
  actorRole: z.string().min(1),
  correctedAt: z.number().int(),
  reason: z.string().trim().min(1).max(BOUNDED_TEXT_MAX_LENGTH),
  paths: z.array(z.string().trim().min(1)).default([]),
  changes: z.array(StaffCorrectionAuditChangeSchema).default([]),
});

export const AdditionalInformationSchema = z.object({
  receivedFinancialAidBefore: z.boolean().default(false),
  previousAidDetails: z.string().nullable().optional(),
  expectedFinancialChanges: z.string().nullable().optional(),
  additionalComments: z.string().nullable().optional(),
  referralSource: z.enum(['returning_family', 'friend', 'rabbi', 'website', 'advertisement', 'other']).nullable().optional(),
  inviteEntrySource: z.enum(['generic_open_cycle']).nullable().optional(),
  parentCollectedFinancialAidSnapshot: ParentCollectedFinancialAidSnapshotSchema.optional(),
  staffInternalNotes: z.array(z.object({
    id: z.string().min(1),
    author: z.string().min(1),
    createdBy: z.string().min(1).optional(),
    createdAt: z.number().int(),
    content: z.string().min(1),
    category: z.string().min(1).optional(),
    tag: z.string().min(1).optional(),
    important: z.boolean().optional(),
  })).default([]),
  staffCorrections: z.array(StaffCorrectionAuditEntrySchema).default([]),
});
export type AdditionalInformation = z.infer<typeof AdditionalInformationSchema>;

// ============================================================================
// Section 16: Document Checklist
// ============================================================================

export const DocumentChecklistItemSchema = z.object({
  documentType: RequiredFinancialDocumentType,
  isRequired: z.boolean().default(true),
  isUploaded: z.boolean().default(false),
  uploadedAt: z.number().int().nullable().optional(), // Unix timestamp
  documentId: z.string().uuid().nullable().optional(), // Reference to uploaded document
  s3Key: z.string().min(1).nullable().optional(),
  fileName: z.string().min(1).nullable().optional(),
  fileType: z.string().min(1).nullable().optional(),
  fileSizeBytes: z.number().int().positive().nullable().optional(),
  confirmedAt: z.number().int().nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type DocumentChecklistItem = z.infer<typeof DocumentChecklistItemSchema>;

export const DocumentChecklistSchema = z.object({
  items: z.array(DocumentChecklistItemSchema).default([]),
  requiredDocumentTypes: z.array(RequiredFinancialDocumentType).optional(),
  requiredDocumentTypesLockedAt: z.number().int().nullable().optional(),
  allRequiredUploaded: z.boolean().default(false), // Computed
  staffOverride: z.boolean().default(false), // Staff can override missing docs
  staffOverrideReason: z.string().nullable().optional(),
});
export type DocumentChecklist = z.infer<typeof DocumentChecklistSchema>;

// ============================================================================
// Section 17: Certification/Signature
// ============================================================================

export const CertificationSchema = z.object({
  certifiedAccurate: z.boolean(), // Must be true to submit
  certifiedBy: z.string().min(1), // Full name
  certificationDate: z.number().int(), // Unix timestamp
  electronicSignature: z.string().min(1), // Typed signature
  ipAddress: z.string().nullable().optional(), // For audit
  userAgent: z.string().nullable().optional(), // For audit
  agreedToTerms: z.boolean(), // Must be true
});
export type Certification = z.infer<typeof CertificationSchema>;
